// Shared SSE parser. Yields one parsed event per `\n\n`-delimited block
// in the response body, preserving the original `event:` name so the
// caller can branch on it.
//
// The parser tolerates the same quirks the previous inline parsers did:
//   - blocks with no `data:` line are skipped
//   - malformed JSON in `data:` is silently dropped
//   - missing `event:` falls back to "message"
//
// Pass `onChunk` to forward the bump-the-idle-timer pulse from
// makeSSEAbort(); without it, a wedged server only fails after the
// outer fetch timeout (if any).

export type SSEEvent<T = unknown> = { event: string; data: T };

export async function* parseSSEEvents<T = unknown>(
  body: ReadableStream<Uint8Array>,
  onChunk?: () => void,
): AsyncGenerator<SSEEvent<T>, void, unknown> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      onChunk?.();
      buffer += decoder.decode(value, { stream: true });
      const messages = buffer.split('\n\n');
      buffer = messages.pop()!;
      for (const message of messages) {
        let currentEvent: string | null = null;
        let dataPayload = '';
        for (const line of message.split('\n')) {
          if (line.startsWith('event:')) currentEvent = line.slice(6).trim();
          else if (line.startsWith('data:')) dataPayload += line.slice(5).trimStart();
        }
        if (!dataPayload) continue;
        let parsed: T;
        try {
          parsed = JSON.parse(dataPayload) as T;
        } catch {
          continue;
        }
        yield { event: currentEvent || 'message', data: parsed };
      }
    }
  } finally {
    // Release the reader lock when the consumer breaks out of the
    // for-await (early return / throw) or when the stream completes.
    // Without this, an early exit would leave the underlying body in
    // a locked state until garbage collection.
    try { reader.releaseLock(); } catch { /* ignore */ }
  }
}
